import { bindActionCreators } from 'redux';
import axios from '../components/shared/axios';
import {
  getAssignment,
  getExercise,
  getVideo,
  getCertificate,
  addVideo,
  addExercise,
  addAssignment,
  addCertificate,
  updateAssignment,
  updateVideo,
  updateExercise,
  updateCertificate
} from './subunitAssetServices';

const baseURL = process.env.REACT_APP_BACKEND_URL;

export const DEFAULT_EXERCISE_PARAMS = {
  duration: 1200000.0,
  deadlineText: '',
  ctaBtnText: 'Download',
  ctaBtnIcon:
    'https://d2oi1rqwb0pj00.cloudfront.net/icons/download-blue.png',
  exerciseIconLink:
    'https://d2oi1rqwb0pj00.cloudfront.net/na-portal/ExerciseThumbnails/exercise-icon-v2.jpg',
  resourceIconLink:
    'https://d2oi1rqwb0pj00.cloudfront.net/na-portal/ExerciseThumbnails/resources.png'
};

export const exerciseIconMap = {
  resource: DEFAULT_EXERCISE_PARAMS.resourceIconLink,
  exercise: DEFAULT_EXERCISE_PARAMS.exerciseIconLink,
  activity: DEFAULT_EXERCISE_PARAMS.exerciseIconLink
};

export const CERTIFICATE_PARAMS_CRITERIAS = {
  cohortondemandCertificate: {
    criteriaWiseResponse: [
      {
        noOfCriteriasMet: 0,
        title: 'Get Started! 🚀',
        description: [
          {
            text:
              'You’ll need to Complete 75% of the course, assignment and course feedback'
          }
        ]
      },
      {
        noOfCriteriasMet: 1,
        title: 'Almost there...💪',
        description: [
          {
            text:
              'Complete the steps here to unlock your personalised certificate'
          }
        ]
      },
      {
        noOfCriteriasMet: 2,
        title: 'Yay Graduation! 🎉',
        description: [
          {
            text: 'You’ve hit the requirements to complete this course.'
          },
          {
            text: '',
            isNewLine: true
          },
          {
            text: 'Get your personalised certificate.'
          }
        ]
      }
    ],
    criteria: [
      {
        text: '75% Progress for On Demand Videos',
        type: 'progress',
        requirement: {
          type: 'percentage',
          value: '75'
        }
      },
      {
        text: 'Complete Feedback',
        type: 'feedback',
        requirement: {
          type: 'status',
          value: 'completed'
        }
      }
    ]
  },
  cohortliveCertificate: {
    criteriaWiseResponse: [
      {
        noOfCriteriasMet: 0,
        title: 'Certificate of Accomplishment',
        description: [
          {
            text: 'You did it! Now go and change the world.'
          },
          {
            text: '',
            isNewLine: true
          },
          {
            text: 'Get your personalised certificate.'
          }
        ]
      }
    ],
    criteria: []
  },
  freeworkshopCertificate: {
    criteriaWiseResponse: [
      {
        noOfCriteriasMet: 0,
        title: 'Get Started! 🚀',
        description: [
          {
            text:
              'Complete the steps below to unlock your personalised certificate'
          }
        ]
      },
      {
        noOfCriteriasMet: 1,
        title: 'Certificate of Accomplishment',
        description: [
          {
            text: 'You did it! Now go and change the world.'
          },
          {
            text: '',
            isNewLine: true
          },
          {
            text: 'Get your personalised certificate.'
          }
        ]
      }
    ],
    criteria: [
      {
        text: 'Complete Feedback',
        type: 'feedback',
        requirement: {
          type: 'status',
          value: 'completed'
        }
      }
    ]
  }
};

const formatExerciseParams = (body) => {
  let descriptionProperty;
  let longDescriptionProperty;
  let titleProperty;
  let ctaBtnTextProperty;
  let ctaBtnIconProperty;

  // Resources don't have long description, exercises do. If resource/exercises titles or description or long description are given from FE, assign them, else use the subunit ones.
  descriptionProperty =
    body['resource-exerciseDescription'] ?? body.description;
  titleProperty = body['resource-exerciseTitle'] ?? body.title;
  ctaBtnTextProperty =
    body.ctaBtnText ?? DEFAULT_EXERCISE_PARAMS.ctaBtnText;
  ctaBtnIconProperty =
    body.ctaBtnIcon ?? DEFAULT_EXERCISE_PARAMS.ctaBtnIcon;
  if (body.category === 'resource') {
    longDescriptionProperty = body['resource-exerciseTitle'] ?? '';
  } else {
    longDescriptionProperty =
      body['resource-exerciseLongDescription'] ?? body.longDescription;
  }

  const formattedBody = {
    code: body.code,
    title: titleProperty,
    description: descriptionProperty,
    duration: body.duation ?? DEFAULT_EXERCISE_PARAMS.duration,
    longDescription: longDescriptionProperty,
    file: { name: body.fileName, link: body.fileLink },
    deadlineText:
      body.deadlineText ?? DEFAULT_EXERCISE_PARAMS.deadlineText,
    category: body.category,
    ctaBtnText: ctaBtnTextProperty,
    ctaBtnIcon: ctaBtnIconProperty
  };
  return formattedBody;
};

const formatCertificateParams = (body) => {
  return {
    ...CERTIFICATE_PARAMS_CRITERIAS[body.certificateType],
    imgData: body.imgData,
    title: body.title ?? 'Certificate'
  };
};

const getSubunitAsset = async (subunit) => {
  if (subunit.videoId) return await getVideo({ _id: subunit.videoId });
  if (subunit.exerciseId)
    return await getExercise({ _id: subunit.exerciseId });
  if (subunit.certificateId)
    return await getCertificate({ _id: subunit.certificateId });
  if (subunit.assignmentId)
    return await getAssignment({ _id: subunit.assignmentId });
};

export const getSubunit = async (params) => {
  return await axios.get(`${baseURL}/subunit`, { params });
};

export const getSubunits = async (pageNo, pageSize, param) => {
  let params = {};
  if (param) params = { pageNo, pageSize, ...param };
  else params = { pageNo, pageSize };
  return await axios.get(`${baseURL}/subunits`, { params });
};

export const updateSubunit = async (subunitCode, params) => {
  return await axios.put(`${baseURL}/subunit/${subunitCode}`, params);
};

export const getSubunitWithAsset = async (params) => {
  const response = await axios.get(`${baseURL}/subunit`, { params });
  const assetResponse = await getSubunitAsset(response.data.subunit);

  return {
    subunit: response.data.subunit,
    asset: assetResponse.data
  };
};

export const addSubunit = async (params) => {
  return await axios.post(`${baseURL}/subunit`, params);
};

export const addAsset = async (params, subunitType) => {
  let paramsFormatted;
  if (subunitType === 'Video') {
    const response = await addVideo(params);
    const videoId = response.data.newVideo._id;
    return { videoId };
  }
  if (subunitType === 'Exercise') {
    paramsFormatted = formatExerciseParams(params);
    const response = await addExercise(paramsFormatted);
    const exerciseId = response.data.newExercise._id;
    return { exerciseId };
  }
  if (subunitType === 'Certificate') {
    const formattedParams = formatCertificateParams(params);
    const response = await addCertificate(formattedParams);
    const certificateId = response.data.newCertificate._id;
    return { certificateId };
  }

  if (subunitType === 'Assignment') {
    const response = await addAssignment(params);
    const assignmentId = response.data.newAssignment._id;
    return { assignmentId };
  }
};

export const updateAsset = async (params, subunitType) => {
  if (subunitType === 'Video') {
    const response = await updateVideo(params);
    const videoId = response.data.video._id;
    return { videoId };
  }
  if (subunitType === 'Exercise') {
    const response = await updateExercise(params);
    const exerciseId = response.data.exercise._id;
    return { exerciseId };
  }
  if (subunitType === 'Certificate') {
    const response = await updateCertificate(params);
    const certificateId = response.data.certificate._id;
    return { certificateId };
  }

  // if (subunitType === 'Assignment') {
  //   const response = await addAssignment(params);
  //   const assignmentId = response.data.assignment._id;
  //   return { assignmentId };
  // }
};
